import { MercatorCoordinate } from 'maplibre-gl';
import { CAMERA_FOLLOW_KERNEL, cameraFollowAllowance } from '../domain/camera-follow';
import { smoothedKeyframeTarget, type KeyframeCameraFollow } from '../domain/keyframe-camera-follow';
import type { ElevationReader } from './night-hawk-camera-geometry';

/** Smooths aim height as well as position, with one shared bound on visible drift. */
export async function keyframeCameraAim(follow: KeyframeCameraFollow | undefined, target: readonly [number, number],
  zoom: number, elevationAt: ElevationReader, signal?: AbortSignal) {
  signal?.throwIfAborted();
  const center = smoothedKeyframeTarget(follow, target, zoom);
  if (!follow || follow.seconds <= 0) return { center, elevation: undefined };
  const origin = MercatorCoordinate.fromLngLat([...target], await elevationAt(target, signal));
  const heights = await Promise.all(CAMERA_FOLLOW_KERNEL.map(async ({ offset, weight }) => {
    const point = follow.targetAt(offset * follow.seconds);
    const height = await elevationAt(point, signal);
    return { z: MercatorCoordinate.fromLngLat([...point], height).z, weight };
  }));
  signal?.throwIfAborted();
  const z = heights.reduce((total, point) => total + point.z * point.weight, 0)
    / heights.reduce((total, point) => total + point.weight, 0);
  const aim = MercatorCoordinate.fromLngLat([...center]);
  const dx = ((aim.x - origin.x + 1.5) % 1) - .5, dy = aim.y - origin.y, dz = z - origin.z;
  const allowance = cameraFollowAllowance(follow.width - follow.padding.left - follow.padding.right,
    follow.height - follow.padding.top - follow.padding.bottom, zoom);
  // Horizontal pursuit already has a smooth bound; add height within the same allowance.
  const remaining = Math.sqrt(Math.max(0, allowance ** 2 - dx ** 2 - dy ** 2));
  const boundedZ = remaining > 0 ? dz * remaining / Math.hypot(remaining, dz) : 0;
  const point = new MercatorCoordinate(origin.x + dx, origin.y + dy, origin.z + boundedZ);
  return { center, elevation: point.toAltitude() };
}
